import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Form, Row, Col, Button, Badge, Modal, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import axios from 'helpers/api';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';

const PRESET_MINUTES = [5, 10, 15, 30, 45, 60];
const DEFAULT_PRE_MINUTES = 15;
const DEFAULT_AT_DOSE = 1;
const DEFAULT_MISSED_MINUTES = 15;

const isPrePreset = (val) => [0, 5, 10, 15, 30, 45, 60].includes(Number(val));
const isMissedPreset = (val) => [5, 10, 15, 30, 45, 60].includes(Number(val));

const NotificationSettings = () => {
  const roleId = Number(localStorage.getItem('roleId'));
  const storedGroupId = localStorage.getItem('groupId');

  // Role permissions per API specification:
  // Role 1 (SuperAdmin) & Role 2 (PharmAdmin): any group
  // Role 4 (GroupAdmin): own group only
  // Role 3 (PharmStaff), 5 (FacilityStaff), 7 (Caregiver): denied
  const isSuperOrPharmAdmin = roleId === 1 || roleId === 2;
  const isGroupAdmin = roleId === 4;
  const hasAccess = isSuperOrPharmAdmin || isGroupAdmin;

  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [preReminderMinutes, setPreReminderMinutes] = useState(DEFAULT_PRE_MINUTES);
  const [atDoseEnabled, setAtDoseEnabled] = useState(DEFAULT_AT_DOSE);
  const [missedAfterMinutes, setMissedAfterMinutes] = useState(DEFAULT_MISSED_MINUTES);

  const [showCustomPre, setShowCustomPre] = useState(false);
  const [showCustomMissed, setShowCustomMissed] = useState(false);

  const [savedSettings, setSavedSettings] = useState({
    pre_reminder_minutes: DEFAULT_PRE_MINUTES,
    at_dose_enabled: DEFAULT_AT_DOSE,
    missed_after_minutes: DEFAULT_MISSED_MINUTES
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Load available groups
  useEffect(() => {
    if (!hasAccess) return;

    const loadGroups = async () => {
      try {
        let stored = JSON.parse(localStorage.getItem('groupsData') || '[]');
        if ((!stored || stored.length === 0) && isSuperOrPharmAdmin) {
          const tokenStr = localStorage.getItem('accessToken');
          const res = await axios.get(
            `${process.env.REACT_APP_SERVER_URL}/facilities/get-all-facility`,
            { headers: { Authorization: `Bearer ${tokenStr}` } }
          );
          const objGrpData = res.data?.data || [];
          stored = objGrpData.map((grp) => ({
            key: grp.name,
            value: grp.id
          }));
          stored.sort((a, b) => a.key.localeCompare(b.key));
          localStorage.setItem('groupsData', JSON.stringify(stored));
        }

        const mappedGroups = stored.map((g) => ({
          value: Number(g.value),
          label: g.key
        }));
        setGroups(mappedGroups);

        if (isGroupAdmin && storedGroupId) {
          const ownGroup = mappedGroups.find((g) => Number(g.value) === Number(storedGroupId)) || {
            value: Number(storedGroupId),
            label: localStorage.getItem('groupName') || `Group #${storedGroupId}`
          };
          setSelectedGroup(ownGroup);
        } else if (isSuperOrPharmAdmin && mappedGroups.length > 0) {
          const initial = storedGroupId
            ? mappedGroups.find((g) => Number(g.value) === Number(storedGroupId)) || mappedGroups[0]
            : mappedGroups[0];
          setSelectedGroup(initial);
        }
      } catch (err) {
        console.error('Error loading groups for notification settings:', err);
      }
    };

    loadGroups();
  }, [hasAccess, isSuperOrPharmAdmin, isGroupAdmin, storedGroupId]);

  // Fetch notification settings for the selected group
  const fetchGroupSettings = useCallback(async (groupId) => {
    if (!groupId) return;
    setLoading(true);
    setError('');

    const tokenStr = localStorage.getItem('accessToken');
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/facilities/notification-settings/${groupId}`,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );

      if (data?.status && data?.data) {
        const pre = Number(data.data.pre_reminder_minutes) ?? DEFAULT_PRE_MINUTES;
        const atDose = Number(data.data.at_dose_enabled) === 1 ? 1 : 0;
        const missed = Number(data.data.missed_after_minutes) ?? DEFAULT_MISSED_MINUTES;

        setPreReminderMinutes(pre);
        setAtDoseEnabled(atDose);
        setMissedAfterMinutes(missed);

        setShowCustomPre(!isPrePreset(pre));
        setShowCustomMissed(!isMissedPreset(missed));

        setSavedSettings({
          pre_reminder_minutes: pre,
          at_dose_enabled: atDose,
          missed_after_minutes: missed
        });
      } else {
        setError(data?.message || 'Failed to retrieve notification settings for this group.');
      }
    } catch (err) {
      const statusCode = err.response?.status;
      const apiMsg = err.response?.data?.message;

      if (statusCode === 403) {
        setError('You are not authorized to view notification settings for this group.');
      } else if (statusCode === 404) {
        setError('Selected group was not found.');
      } else if (statusCode === 400) {
        setError(apiMsg || 'Invalid group requested.');
      } else {
        setError(apiMsg || 'Error fetching notification settings. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedGroup?.value) {
      fetchGroupSettings(selectedGroup.value);
    }
  }, [selectedGroup, fetchGroupSettings]);

  // Check if form is dirty (has unsaved changes)
  const isDirty = useMemo(() => {
    return (
      preReminderMinutes !== savedSettings.pre_reminder_minutes ||
      atDoseEnabled !== savedSettings.at_dose_enabled ||
      missedAfterMinutes !== savedSettings.missed_after_minutes
    );
  }, [preReminderMinutes, atDoseEnabled, missedAfterMinutes, savedSettings]);

  // Validation — Limited to 60 mins
  const validateField = (val) => {
    if (val === '' || val === null || val === undefined || isNaN(val)) {
      return 'Must be a valid number.';
    }
    const num = Number(val);
    if (!Number.isInteger(num)) {
      return 'Must be a whole number of minutes.';
    }
    if (num < 0 || num > 60) {
      return 'Must be between 0 and 60 minutes.';
    }
    return null;
  };

  const preError = validateField(preReminderMinutes);
  const missedError = validateField(missedAfterMinutes);
  const isFormValid = !preError && !missedError && selectedGroup?.value;

  // Handle Discard
  const handleDiscard = () => {
    setPreReminderMinutes(savedSettings.pre_reminder_minutes);
    setAtDoseEnabled(savedSettings.at_dose_enabled);
    setMissedAfterMinutes(savedSettings.missed_after_minutes);
    setShowCustomPre(!isPrePreset(savedSettings.pre_reminder_minutes));
    setShowCustomMissed(!isMissedPreset(savedSettings.missed_after_minutes));
  };

  // Trigger Confirmation Modal
  const handleOpenConfirmModal = (e) => {
    if (e) e.preventDefault();
    if (!isFormValid) return;
    setShowConfirmModal(true);
  };

  // Perform Save
  const handleConfirmSave = async () => {
    if (!selectedGroup?.value) return;

    setSaving(true);
    const tokenStr = localStorage.getItem('accessToken');

    const payload = {
      group_id: Number(selectedGroup.value),
      pre_reminder_minutes: Number(preReminderMinutes),
      at_dose_enabled: atDoseEnabled === 1 ? 1 : 0,
      missed_after_minutes: Number(missedAfterMinutes)
    };

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/facilities/update-notification-settings`,
        payload,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );

      if (data?.status) {
        toast.success(data.message || 'Notification settings saved successfully.');
        setSavedSettings({
          pre_reminder_minutes: Number(preReminderMinutes),
          at_dose_enabled: atDoseEnabled === 1 ? 1 : 0,
          missed_after_minutes: Number(missedAfterMinutes)
        });
        setShowConfirmModal(false);
      } else {
        toast.error(data?.message || 'Failed to save notification settings.');
      }
    } catch (err) {
      const apiMsg = err.response?.data?.message || 'An error occurred while saving notification settings.';
      toast.error(apiMsg);
    } finally {
      setSaving(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="p-3">
        <Alert variant="warning" className="d-flex align-items-center">
          <FontAwesomeIcon icon="ban" className="me-2 text-warning fs-8" />
          <div>
            <strong>Access Denied:</strong> Notification timing settings affect medication safety and are only accessible by Administrators.
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-3">
      {/* Group Selection Header for SuperAdmin & PharmAdmin */}
      {isSuperOrPharmAdmin && (
        <Row className="mb-4 align-items-center">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-semibold">
                Select Group <span className="text-danger">*</span>
              </Form.Label>
              <ThemedSelect
                options={groups}
                value={selectedGroup}
                onChange={(option) => {
                  if (isDirty) {
                    if (window.confirm('You have unsaved changes. Discard and switch group?')) {
                      setSelectedGroup(option);
                    }
                  } else {
                    setSelectedGroup(option);
                  }
                }}
                placeholder="Choose a Group..."
                isClearable={false}
              />
            </Form.Group>
          </Col>
        </Row>
      )}

      {/* Loading state */}
      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-secondary">Loading group notification settings...</p>
        </div>
      )}

      {/* Error alert */}
      {error && !loading && (
        <Alert variant="danger" className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
            {error}
          </div>
          {selectedGroup?.value && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => fetchGroupSettings(selectedGroup.value)}
            >
              Retry
            </Button>
          )}
        </Alert>
      )}

      {/* Main Settings Form */}
      {!loading && !error && selectedGroup && (
        <>
          <Row className="g-4">
            <Col>
              <Card className="border">
                <Card.Header className="bg-light py-3 border-bottom">
                  <h6 className="mb-0 fw-bold">
                    <FontAwesomeIcon icon="bell" className="me-2 text-primary" />
                    Timing Configuration (Relative to Dose)
                  </h6>
                </Card.Header>
                <Card.Body className="p-4">
                  {/* 1. Pre-dose Warning */}
                  <div className="mb-4 pb-3 border-bottom">
                    <div className="d-flex justify-content-between align-items-baseline mb-2">
                      <Form.Label className="fw-semibold mb-0">
                        1 · Pre-Dose Warning
                      </Form.Label>
                      <span className="badge bg-primary-subtle text-primary fw-medium">
                        {preReminderMinutes === 0 ? 'Due Now (0m)' : `${preReminderMinutes} min before`}
                      </span>
                    </div>
                    <p className="text-muted small mb-2">
                      Minutes before a scheduled dose to send the initial warning reminder (0–60 min).
                    </p>

                    {/* Preset Chips + Custom Button */}
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      {PRESET_MINUTES.map((preset) => (
                        <Button
                          key={preset}
                          type="button"
                          size="sm"
                          variant={!showCustomPre && Number(preReminderMinutes) === preset ? 'primary' : 'outline-secondary'}
                          className="py-1 px-2"
                          onClick={() => {
                            setPreReminderMinutes(preset);
                            setShowCustomPre(false);
                          }}
                        >
                          {preset} min
                        </Button>
                      ))}
                      <Button
                        type="button"
                        size="sm"
                        variant={showCustomPre ? 'primary' : 'outline-primary'}
                        className="py-1 px-2"
                        onClick={() => setShowCustomPre((prev) => !prev)}
                      >
                        Custom
                      </Button>
                    </div>

                    {/* Custom Time Edit Field with ±5 min arrows */}
                    {showCustomPre && (
                      <div className="mt-2 p-2 bg-light rounded border" style={{ maxWidth: '280px' }}>
                        <Form.Label className="small fw-semibold text-secondary mb-1">
                          Custom Minutes (0–60 min):
                        </Form.Label>
                        <InputGroup size="sm">
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              const current = Number(preReminderMinutes) || 0;
                              const next = Math.max(0, current - 5);
                              setPreReminderMinutes(next);
                            }}
                            disabled={Number(preReminderMinutes) <= 0}
                            title="Decrease by 5 minutes"
                          >
                            <FontAwesomeIcon icon="minus" />
                          </Button>
                          <Form.Control
                            type="number"
                            min="0"
                            max="60"
                            step="5"
                            className="text-center fw-bold"
                            value={preReminderMinutes}
                            onChange={(e) => {
                              const val = e.target.value;
                              setPreReminderMinutes(val === '' ? '' : Number(val));
                            }}
                            isInvalid={!!preError}
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              const current = Number(preReminderMinutes) || 0;
                              const next = Math.min(60, current + 5);
                              setPreReminderMinutes(next);
                            }}
                            disabled={Number(preReminderMinutes) >= 60}
                            title="Increase by 5 minutes"
                          >
                            <FontAwesomeIcon icon="plus" />
                          </Button>
                          <InputGroup.Text>mins</InputGroup.Text>
                        </InputGroup>
                        {preError && <div className="text-danger small mt-1">{preError}</div>}
                      </div>
                    )}
                  </div>

                  {/* 2. Missed-dose Alert */}
                  <div className="mb-2">
                    <div className="d-flex justify-content-between align-items-baseline mb-2">
                      <Form.Label className="fw-semibold mb-0">
                        2 · Missed-Dose Alert
                      </Form.Label>
                      <span className="badge bg-danger-subtle text-danger fw-medium">
                        {missedAfterMinutes} min after
                      </span>
                    </div>
                    <p className="text-muted small mb-2">
                      Minutes after the scheduled dose time before sending the missed alert (0–60 min).
                    </p>

                    {/* Preset Chips + Custom Button */}
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      {PRESET_MINUTES.map((preset) => (
                        <Button
                          key={preset}
                          type="button"
                          size="sm"
                          variant={!showCustomMissed && Number(missedAfterMinutes) === preset ? 'danger' : 'outline-secondary'}
                          className="py-1 px-2"
                          onClick={() => {
                            setMissedAfterMinutes(preset);
                            setShowCustomMissed(false);
                          }}
                        >
                          {preset} min
                        </Button>
                      ))}
                      <Button
                        type="button"
                        size="sm"
                        variant={showCustomMissed ? 'danger' : 'outline-danger'}
                        className="py-1 px-2"
                        onClick={() => setShowCustomMissed((prev) => !prev)}
                      >
                        Custom
                      </Button>
                    </div>

                    {/* Custom Time Edit Field with ±5 min arrows */}
                    {showCustomMissed && (
                      <div className="mt-2 p-2 bg-light rounded border" style={{ maxWidth: '280px' }}>
                        <Form.Label className="small fw-semibold text-secondary mb-1">
                          Custom Minutes (0–60 min):
                        </Form.Label>
                        <InputGroup size="sm">
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              const current = Number(missedAfterMinutes) || 0;
                              const next = Math.max(0, current - 5);
                              setMissedAfterMinutes(next);
                            }}
                            disabled={Number(missedAfterMinutes) <= 0}
                            title="Decrease by 5 minutes"
                          >
                            <FontAwesomeIcon icon="minus" />
                          </Button>
                          <Form.Control
                            type="number"
                            min="0"
                            max="60"
                            step="5"
                            className="text-center fw-bold"
                            value={missedAfterMinutes}
                            onChange={(e) => {
                              const val = e.target.value;
                              setMissedAfterMinutes(val === '' ? '' : Number(val));
                            }}
                            isInvalid={!!missedError}
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              const current = Number(missedAfterMinutes) || 0;
                              const next = Math.min(60, current + 5);
                              setMissedAfterMinutes(next);
                            }}
                            disabled={Number(missedAfterMinutes) >= 60}
                            title="Increase by 5 minutes"
                          >
                            <FontAwesomeIcon icon="plus" />
                          </Button>
                          <InputGroup.Text>mins</InputGroup.Text>
                        </InputGroup>
                        {missedError && <div className="text-danger small mt-1">{missedError}</div>}
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Action Footer Bar */}
          <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
            <div>
              {isDirty && (
                <Button
                  // variant="outline-secondary"
                  variant={'outline-danger'}
                  size="sm"
                  onClick={handleDiscard}
                  disabled={saving}
                >
                  <FontAwesomeIcon icon="undo" className="me-1" />
                  Discard Changes
                </Button>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                className="px-4 fw-semibold"
                disabled={!isFormValid || saving || !isDirty}
                onClick={handleOpenConfirmModal}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Settings'
                )}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Explicit Confirmation Dialog */}
      <Modal
        show={showConfirmModal}
        onHide={() => !saving && setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title className="fs-9">Confirm Notification Timing</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-secondary mb-3">
            Are you sure you want to update the medpass notification timing settings for{' '}
            <strong>{selectedGroup?.label || `Group #${selectedGroup?.value}`}</strong>?
          </p>

          <Card className="bg-light border-0 mb-3">
            <Card.Body className="py-2 px-3 small">
              <Row className="py-1 border-bottom">
                <Col xs={6} className="text-muted">Pre-Dose Warning:</Col>
                <Col xs={6} className="fw-bold text-end">
                  {preReminderMinutes === 0 ? 'Due now (0 min)' : `${preReminderMinutes} min before`}
                </Col>
              </Row>
              <Row className="py-1">
                <Col xs={6} className="text-muted">Missed-Dose Alert:</Col>
                <Col xs={6} className="fw-bold text-end text-danger">
                  {missedAfterMinutes} min after
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <div className="text-muted small">
            <FontAwesomeIcon icon="info-circle" className="me-1 text-info" />
            These timings will take effect for all facilities in this group.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowConfirmModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirmSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Confirm & Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default NotificationSettings;
